import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  TextField,
  useTheme
} from '@mui/material';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';
import { SV } from '../automation/stratovaiTestIds';

const ProjectSelection = ({ authenticatedUser, onProjectSelect, onLogout, apiClient }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('all');
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();

  useEffect(() => {
    const fetchUserProjects = async () => {
      try {
        const data = await apiClient.getProjects(authenticatedUser.user_id);
        setProjects(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    if (authenticatedUser) {
      fetchUserProjects();
    }
  }, [authenticatedUser, apiClient]);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    onProjectSelect(project);
  };

  const getProjectIcon = (projectType) => {
    switch (projectType) {
      case 'ui_exploration':
        return '🔍';
      case 'saucedemo':
        return '🛒';
      case 'orangehrm':
        return '👥';
      default:
        return '📋';
    }
  };

  const getProjectTypeLabel = (projectType) => {
    switch (projectType) {
      case 'ui_exploration':
        return 'UI Exploration';
      case 'saucedemo':
        return 'E-commerce';
      case 'orangehrm':
        return 'HR Management';
      default:
        return 'Custom';
    }
  };

  const isSuperAdmin = (authenticatedUser?.role || '').toLowerCase() === 'super_admin';

  const companyOptions = useMemo(() => {
    const byKey = new Map();
    projects.forEach((project) => {
      const company = project.company;
      if (!company) {
        return;
      }
      const key = company.company_id || company.db_id || company.company_name;
      if (!key || byKey.has(key)) {
        return;
      }
      byKey.set(key, {
        key,
        name: company.company_name || 'Unknown Company',
        companyId: company.company_id || '',
        dbId: company.db_id || '',
      });
    });
    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return [...projects]
      .filter((project) => {
        if (selectedCompanyFilter === 'all') {
          return true;
        }
        return (project.company?.company_id || project.company?.db_id || '') === selectedCompanyFilter;
      })
      .filter((project) => {
        if (!query) {
          return true;
        }
        const company = project.company || {};
        const searchable = [
          project.project_name,
          project.project_key,
          project.base_url,
          project.description,
          company.company_name,
          company.company_id,
          company.db_id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchable.includes(query);
      })
      .sort((a, b) => {
        const companyA = (a.company?.company_name || '').toLowerCase();
        const companyB = (b.company?.company_name || '').toLowerCase();
        if (companyA !== companyB) {
          return companyA.localeCompare(companyB);
        }
        return (a.project_name || '').localeCompare(b.project_name || '');
      });
  }, [projects, searchQuery, selectedCompanyFilter]);

  const formatCompanyMeta = (company) => {
    if (!company) {
      return null;
    }
    const parts = [];
    if (company.company_id) {
      parts.push(`ID: ${company.company_id}`);
    }
    if (company.db_id) {
      parts.push(`DB: ${company.db_id}`);
    }
    return parts.join(' • ');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDarkMode 
            ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading projects...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid={SV.app.projectSelection}
      sx={{
        minHeight: '100vh',
        background: isDarkMode 
          ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 4,
            p: 3,
            background: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: theme.shadows[4],
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                         <img
               src="/logo.png"
               alt="Stratovai Logo"
               style={{ 
                 height: '2.5rem', 
                 width: 'auto',
                 filter: isDarkMode ? 'drop-shadow(0 1px 2px rgba(255,255,255,0.1))' : 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
               }}
             />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  background: 'linear-gradient(135deg, #0099F7 0%, #0052D4 100%)',
                  fontWeight: 600
                }}
              >
                {authenticatedUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h4" component="h1" fontWeight={600}>
                  Select Project
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Welcome back, {authenticatedUser.full_name}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Button
            variant="outlined"
            onClick={onLogout}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              '&:hover': {
                borderColor: theme.palette.error.dark,
                backgroundColor: theme.palette.error.light + '20'
              }
            }}
          >
            Logout
          </Button>
        </Box>

        <Box
          sx={{
            mb: 3,
            p: 2,
            background: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: theme.shadows[2],
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              label="Search projects, companies, or IDs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 280, flex: 1 }}
            />
            <Chip
              label={`${filteredProjects.length} of ${projects.length} projects`}
              color="primary"
              variant="outlined"
            />
            {isSuperAdmin && (
              <Chip
                label={`${companyOptions.length} companies`}
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>

          {isSuperAdmin && companyOptions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="All companies"
                color={selectedCompanyFilter === 'all' ? 'primary' : 'default'}
                variant={selectedCompanyFilter === 'all' ? 'filled' : 'outlined'}
                onClick={() => setSelectedCompanyFilter('all')}
              />
              {companyOptions.map((company) => (
                <Chip
                  key={company.key}
                  label={`${company.name}${company.companyId ? ` • ${company.companyId}` : ''}`}
                  color={selectedCompanyFilter === company.key ? 'primary' : 'default'}
                  variant={selectedCompanyFilter === company.key ? 'filled' : 'outlined'}
                  onClick={() => setSelectedCompanyFilter(company.key)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Projects Grid */}
        <Grid container spacing={3} data-testid={SV.app.projectGrid}>
          {filteredProjects.map((project) => (
            <Grid xs={12} sm={6} md={4} key={project.project_id}>
              <Card
                data-testid={SV.app.projectCard}
                data-sv-project-id={project.project_id}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: selectedProject?.project_id === project.project_id 
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                    borderColor: theme.palette.primary.main
                  },
                  ...(selectedProject?.project_id === project.project_id && {
                    boxShadow: `0 8px 32px ${theme.palette.primary.main}40`
                  })
                }}
                onClick={() => handleProjectSelect(project)}
              >
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        fontSize: '2rem',
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #0099F7 0%, #0052D4 100%)',
                        borderRadius: 2,
                        color: 'white'
                      }}
                    >
                      {getProjectIcon(project.project_type)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" component="h3" fontWeight={600} gutterBottom>
                        {project.project_name}
                      </Typography>
                      {project.company?.company_name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {project.company.company_name}
                        </Typography>
                      )}
                      <Chip
                        label={getProjectTypeLabel(project.project_type)}
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #43E6C2 0%, #26A69A 100%)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </Box>

                  {project.company && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Chip
                        label={project.company.company_name}
                        size="small"
                        variant="outlined"
                      />
                      {formatCompanyMeta(project.company) && (
                        <Chip
                          label={formatCompanyMeta(project.company)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}

                  {project.project_key && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1, fontFamily: 'monospace' }}
                    >
                      Project Key: {project.project_key}
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      mb: 2,
                      wordBreak: 'break-all'
                    }}
                  >
                    {project.base_url}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flex: 1, mb: 2 }}
                  >
                    {project.description || 'No description available'}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'Unknown'}
                    </Typography>
                    {selectedProject?.project_id === project.project_id && (
                      <Chip
                        label="Selected"
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredProjects.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              background: theme.palette.background.paper,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No matching projects found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a different search term or company filter.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ProjectSelection; 